// Models
// Utilities
import { Temporal } from "temporal-polyfill-lite";
import cloneDeep from "lodash/cloneDeep";
import { PatchableODataEntity } from "../common/PatchableODataEntity";
/**
 * The task chat message implementation.
 * TODO PBI 2512594 implement ICreatable, IUpdatable, IDeletable
 */ export class ChatMessage extends PatchableODataEntity {
    mapEntityChangesToResource(changed, source) {
        return {
            content: changed.content,
            properties: changed.properties && this.serializeMessageProperties()
        };
    }
    /**
     * Calculates the promoted properties from the given state and diff.
     * @param state The baseline state of the entity
     * @param diff The entity change to process for promoted property changes
     * @returns A diff containing all promoted properties
     */ static calculatePromotedProperties(state, diff) {
        // Promoted properties are only 'isEdited', 'isDeleted', and 'deletedTime' for now
        let promotedDiff = {};
        if (diff.content != null && diff.content !== state.content) {
            promotedDiff = {
                ...promotedDiff,
                isEdited: true
            };
        }
        return promotedDiff;
    }
    static fromResource(resource) {
        return new ChatMessage({
            id: resource.id,
            version: resource["@etag"],
            threadType: resource.threadType,
            parentEntityId: resource.parentEntityId,
            createdBy: {
                user: {
                    id: ChatMessage.parseUserId(resource.createdBy.user.id),
                    tenantId: resource.createdBy.user.tenantId
                },
                application: resource.createdBy.application
            },
            createdDateTime: Temporal.Instant.from(resource.createdDateTime),
            deletedTime: resource.deletedTime ? Temporal.Instant.from(resource.deletedTime) : null,
            isDeleted: resource.isDeleted,
            editedTime: resource.editedTime ? Temporal.Instant.from(resource.editedTime) : null,
            isEdited: resource.isEdited,
            messageType: resource.messageType,
            content: resource.content,
            properties: {
                mentions: resource.properties.mentions,
                reactions: resource.properties.reactions.map((reaction)=>({
                        key: reaction.key,
                        users: reaction.users.map((user)=>({
                                createdBy: user.createdBy,
                                createdDateTime: Temporal.Instant.from(user.createdDateTime)
                            }))
                    }))
            }
        });
    }
    getClone() {
        return new ChatMessage({
            id: this.id,
            version: this.version,
            threadType: this.threadType,
            parentEntityId: this.parentEntityId,
            createdBy: this.createdBy,
            createdDateTime: Temporal.Instant.from(this.createdDateTime),
            deletedTime: this.deletedTime ? Temporal.Instant.from(this.deletedTime) : null,
            isDeleted: this.isDeleted,
            editedTime: this.editedTime ? Temporal.Instant.from(this.editedTime) : null,
            isEdited: this.isEdited,
            messageType: this.messageType,
            content: this.content,
            properties: {
                mentions: cloneDeep(this.properties.mentions),
                reactions: cloneDeep(this.properties.reactions)
            }
        });
    }
    /**
     * Sets a mutable property of the chat message.
     * Throws an error if the property is read-only.
     * TODO PBI 2512594 implement a more robust property validation for each message.properties sub-object filed too (like reactions, mentions, etc.)
     * probably using impls for mentions/reactions/quotedMsgs etc.
     * @param key - The property key to set.
     * @param value - The value to set for the property.
     * @returns A new instance of IChatMessage with the updated property.
     */ setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            parentEntityId: this.parentEntityId,
            createdBy: this.createdBy,
            createdDateTime: this.createdDateTime.toString(),
            deletedTime: this.deletedTime ? this.deletedTime.toString() : null,
            isDeleted: this.isDeleted,
            editedTime: this.editedTime ? this.editedTime.toString() : null,
            isEdited: this.isEdited,
            threadType: this.threadType,
            messageType: this.messageType,
            content: this.content,
            properties: this.serializeMessageProperties(),
            embeddedImageReferences: []
        };
    }
    forCreate() {
        return {
            messageType: this.messageType,
            content: this.content,
            properties: this.serializeMessagePropertiesForCreate()
        };
    }
    forUpdate(source) {
        // Use parent implementation to check if there are changes
        const changes = super.forUpdate(source);
        if (changes == null) {
            return null;
        }
        return {
            id: this.id,
            version: this.version,
            // Edit message uses PUT operations, so send full message payload instead of only diffs
            changes: this.forCreate()
        };
    }
    getUniqueId() {
        return ChatMessage.createUniqueMessageId(this.parentEntityId, this.id);
    }
    /**
     * Serializes the message properties bag field to match the expected format for the service.
     * @returns An object containing the serialized message properties bag.
     */ serializeMessageProperties() {
        return {
            mentions: this.properties.mentions.map((mention)=>({
                    index: mention.index,
                    mentioned: mention.mentioned,
                    type: mention.type
                })),
            reactions: this.properties.reactions.map((reaction)=>({
                    key: reaction.key,
                    users: reaction.users.map((user)=>({
                            createdBy: user.createdBy,
                            createdDateTime: user.createdDateTime.toString()
                        }))
                }))
        };
    }
    /** Serializes the message properties bag for create requests. Reactions are excluded because they cannot be set during creation */ serializeMessagePropertiesForCreate() {
        const properties = this.serializeMessageProperties();
        return {
            mentions: properties.mentions
        };
    }
    /** Parse user ID from service which could be in a different format */ static parseUserId(userId) {
        // Only keep ID at the end, after ":orgid:"
        const parts = userId.split(":orgid:");
        return parts.length > 1 ? parts[1] : userId;
    }
    /** Get a unique message ID by combining parent entity ID and message ID */ static createUniqueMessageId(parentEntityId, messageId) {
        return `${parentEntityId}|${messageId}`;
    }
    constructor(propertyBag){
        super(propertyBag);
        this.threadType = propertyBag.threadType;
        this.parentEntityId = propertyBag.parentEntityId;
        this.createdBy = propertyBag.createdBy;
        this.createdDateTime = propertyBag.createdDateTime;
        this.deletedTime = propertyBag.deletedTime;
        this.isDeleted = propertyBag.isDeleted;
        this.editedTime = propertyBag.editedTime;
        this.isEdited = propertyBag.isEdited;
        this.messageType = propertyBag.messageType;
        this.content = propertyBag.content;
        this.properties = propertyBag.properties;
    }
}
